import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Card,
  TextField,
  MenuItem,
  Typography,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  InputAdornment,
  Link,
} from '@mui/material'
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  SwapHoriz as SwapHorizIcon,
  Build as AdjustIcon,
  Clear as ClearIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useSnackbar } from 'notistack'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'

interface Movement {
  id: string
  product_name: string | null
  variant_name: string | null
  variant_sku: string | null
  movement_type: string | null
  quantity: number
  stock_before: number | null
  stock_after: number | null
  reason: string | null
  reference_id: string | null
  reference_type: string | null
  reference_number: string | null   // resolved doc number e.g. SO-2024-001
  reference_path: string | null     // admin route segment e.g. "documents/invoices"
  warehouse_name: string | null
  created_by: string                // now admin full_name
  created_at: string
}

interface Warehouse {
  id: string
  name: string
}

const MOVEMENT_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'purchase', label: 'Purchase (Stock In)' },
  { value: 'sale', label: 'Sale (Stock Out)' },
  { value: 'adjustment', label: 'Adjustment' },
  { value: 'return', label: 'Return' },
  { value: 'transfer', label: 'Transfer' },
]

const TYPE_META: Record<string, { label: string; color: 'success' | 'error' | 'warning' | 'info' | 'default'; icon: React.ReactNode }> = {
  purchase: { label: 'Purchase', color: 'success', icon: <TrendingUpIcon fontSize="small" color="success" /> },
  sale:     { label: 'Sale',     color: 'error',   icon: <TrendingDownIcon fontSize="small" color="error" /> },
  return:   { label: 'Return',   color: 'info',    icon: <TrendingUpIcon fontSize="small" color="info" /> },
  transfer: { label: 'Transfer', color: 'info',    icon: <SwapHorizIcon fontSize="small" color="info" /> },
  adjustment:{ label: 'Adjust', color: 'warning',  icon: <AdjustIcon fontSize="small" color="warning" /> },
}

const StockMovementHistory: React.FC = () => {
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(50)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [movementType, setMovementType] = useState('all')
  const [warehouseId, setWarehouseId] = useState('')
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [movements, setMovements] = useState<Movement[]>([])
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [warehouses, setWarehouses] = useState<Warehouse[]>([])

  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()

  useEffect(() => {
    api.get('/admin/inventory/warehouses').then(r => setWarehouses(r.data || [])).catch(() => {})
  }, [])

  const loadMovements = useCallback(async () => {
    setIsLoading(true)
    try {
      const params: Record<string, any> = { limit: pageSize, offset: page * pageSize }
      if (search) params.search = search
      if (movementType !== 'all') params.movement_type = movementType
      if (warehouseId) params.warehouse_id = warehouseId
      if (startDate) params.start_date = startDate.toISOString()
      if (endDate) params.end_date = endDate.toISOString()

      const resp = await api.get('/admin/inventory/movements', { params })
      setMovements(resp.data.items || [])
      setTotal(resp.data.total || 0)
    } catch {
      enqueueSnackbar('Failed to load stock movements', { variant: 'error' })
      setMovements([])
      setTotal(0)
    } finally {
      setIsLoading(false)
    }
  }, [page, pageSize, search, movementType, warehouseId, startDate, endDate, enqueueSnackbar])

  useEffect(() => { loadMovements() }, [loadMovements])

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(0) }, 400)
    return () => clearTimeout(t)
  }, [searchInput])

  const handleExport = async () => {
    setIsExporting(true)
    try {
      const params: Record<string, any> = { format: 'excel' }
      if (search) params.search = search
      if (movementType !== 'all') params.movement_type = movementType
      if (warehouseId) params.warehouse_id = warehouseId
      if (startDate) params.start_date = startDate.toISOString().split('T')[0]
      if (endDate) params.end_date = endDate.toISOString().split('T')[0]

      const resp = await api.get('/admin/inventory/reports/stock-history', { params, responseType: 'blob' })
      const url = window.URL.createObjectURL(resp.data)
      const a = document.createElement('a')
      a.href = url
      a.download = `stock_movements_${new Date().toISOString().split('T')[0]}.xlsx`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
      enqueueSnackbar('Export downloaded', { variant: 'success' })
    } catch {
      enqueueSnackbar('Export failed', { variant: 'error' })
    } finally {
      setIsExporting(false)
    }
  }

  const clearFilters = () => {
    setSearchInput(''); setSearch(''); setMovementType('all')
    setWarehouseId(''); setStartDate(null); setEndDate(null); setPage(0)
  }
  const hasFilters = search || movementType !== 'all' || warehouseId || startDate || endDate

  const openDoc = (m: Movement) => {
    if (m.reference_path && m.reference_id) {
      navigate(`/admin/${m.reference_path}/${m.reference_id}`)
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>Stock Movement History</Typography>
            <Typography variant="body2" color="text.secondary">
              {total.toLocaleString()} movements — trace every stock change to its source document
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh">
              <IconButton onClick={loadMovements} disabled={isLoading} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={isExporting}
              size="small"
              sx={{ textTransform: 'none' }}
            >
              {isExporting ? 'Exporting…' : 'Export Excel'}
            </Button>
          </Stack>
        </Box>

        {/* Filters */}
        <Card variant="outlined" sx={{ p: 1.5, mb: 1.5, borderRadius: 2 }}>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
            <TextField
              placeholder="Search product, SKU, reason, doc number…"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              size="small"
              sx={{ minWidth: 260, flexGrow: 1 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment>,
                endAdornment: searchInput ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => { setSearchInput(''); setSearch(''); setPage(0) }}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
            <TextField
              select label="Type" value={movementType}
              onChange={e => { setMovementType(e.target.value); setPage(0) }}
              size="small" sx={{ minWidth: 160 }}
            >
              {MOVEMENT_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
            {warehouses.length > 0 && (
              <TextField
                select label="Warehouse" value={warehouseId}
                onChange={e => { setWarehouseId(e.target.value); setPage(0) }}
                size="small" sx={{ minWidth: 150 }}
              >
                <MenuItem value="">All Warehouses</MenuItem>
                {warehouses.map(w => <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>)}
              </TextField>
            )}
            <DatePicker
              label="From" value={startDate} onChange={d => { setStartDate(d); setPage(0) }}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 130 } } }}
            />
            <DatePicker
              label="To" value={endDate} onChange={d => { setEndDate(d); setPage(0) }}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 130 } } }}
            />
            {hasFilters && (
              <Button size="small" onClick={clearFilters} color="inherit" startIcon={<ClearIcon />} sx={{ textTransform: 'none' }}>
                Clear
              </Button>
            )}
          </Stack>
        </Card>

        {isLoading && <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />}

        {/* Table */}
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 320px)' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {['Date & Time', 'Product / SKU', 'Type', 'Qty', 'Before → After', 'Source Document', 'Reason / Notes', 'Warehouse', 'By'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem', whiteSpace: 'nowrap', backgroundColor: 'action.hover' }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {movements.length === 0 && !isLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">No movements found</Typography>
                    </TableCell>
                  </TableRow>
                ) : movements.map(m => {
                  const meta = TYPE_META[m.movement_type?.toLowerCase() || ''] || { label: m.movement_type || '—', color: 'default', icon: null }
                  return (
                    <TableRow key={m.id} hover>
                      {/* Date */}
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" fontWeight={500}>
                          {new Date(m.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      </TableCell>

                      {/* Product */}
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>{m.product_name || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                          {[m.variant_name, m.variant_sku].filter(Boolean).join(' · ')}
                        </Typography>
                      </TableCell>

                      {/* Type */}
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {meta.icon}
                          <Chip
                            label={meta.label}
                            color={meta.color}
                            size="small"
                            variant="outlined"
                            sx={{ borderRadius: 1, fontSize: '0.65rem', height: 20 }}
                          />
                        </Stack>
                      </TableCell>

                      {/* Qty */}
                      <TableCell>
                        <Typography
                          variant="body2" fontWeight={700}
                          color={m.quantity > 0 ? 'success.main' : 'error.main'}
                        >
                          {m.quantity > 0 ? '+' : ''}{m.quantity}
                        </Typography>
                      </TableCell>

                      {/* Before → After */}
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" color="text.secondary" component="span">
                          {m.stock_before ?? '—'}
                        </Typography>
                        <Typography variant="body2" component="span" sx={{ mx: 0.5 }}>→</Typography>
                        <Typography variant="body2" fontWeight={600} component="span">
                          {m.stock_after ?? '—'}
                        </Typography>
                      </TableCell>

                      {/* Source Document — the key column */}
                      <TableCell sx={{ minWidth: 140 }}>
                        {m.reference_number ? (
                          <Tooltip title={`${m.reference_type} · Click to open`}>
                            <Link
                              component="button"
                              variant="body2"
                              fontWeight={600}
                              underline="hover"
                              color="primary"
                              onClick={() => openDoc(m)}
                              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                            >
                              {m.reference_number}
                              <OpenInNewIcon sx={{ fontSize: 12, opacity: 0.6 }} />
                            </Link>
                          </Tooltip>
                        ) : m.reference_type ? (
                          <Typography variant="caption" color="text.secondary">
                            {m.reference_type}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>

                      {/* Reason */}
                      <TableCell sx={{ maxWidth: 180 }}>
                        <Typography variant="body2" noWrap title={m.reason || ''}>
                          {m.reason || '—'}
                        </Typography>
                      </TableCell>

                      {/* Warehouse */}
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                          {m.warehouse_name || '—'}
                        </Typography>
                      </TableCell>

                      {/* By — now full admin name */}
                      <TableCell>
                        <Typography variant="caption" noWrap sx={{ maxWidth: 110, display: 'block' }}>
                          {m.created_by || 'System'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[20, 50, 100, 200]}
            component="div"
            count={total}
            rowsPerPage={pageSize}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={e => { setPageSize(parseInt(e.target.value, 10)); setPage(0) }}
            sx={{ borderTop: '1px solid', borderColor: 'divider' }}
          />
        </Paper>
      </Box>
    </LocalizationProvider>
  )
}

export default StockMovementHistory
